// components/ki/KiTextHelfer.tsx
// Wiederverwendbares KI-Panel für Plain-Text-Felder (Ticket-Kommentar/Beschreibung) —
// Bedienmuster wie das KI-Panel im Mail-Composer (EmailVerfassen): Anweisung → Vorschau → Übernehmen.
import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase/client';
import { FELD, FORM, KNOPF } from '../island/form/form-stil';

export interface KiTextHelferProps {
  aktuellerText: string;
  kontext: { ticketId?: string; titel?: string };
  onUebernehmen: (text: string) => void;
  /** Öffnungsrichtung des Panels; 'oben' (Default) für Felder am Seitenende. */
  richtung?: 'oben' | 'unten';
  /** Dunkle Optik für die Pillen-Formulare (Tokens aus components/island/form/form-stil). Ohne Angabe hell wie bisher. */
  dunkel?: boolean;
}

const CHIPS = ['Kürzer', 'Förmlicher', 'Rechtschreibung korrigieren'];

export default function KiTextHelfer({ aktuellerText, kontext, onUebernehmen, richtung = 'oben', dunkel = false }: KiTextHelferProps) {
  const [offen, setOffen] = useState(false);
  const [anweisung, setAnweisung] = useState('');
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [laedt, setLaedt] = useState<'ueberarbeiten' | 'entwerfen' | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const rufen = async (modus: 'ueberarbeiten' | 'entwerfen') => {
    setLaedt(modus); setFehler(null);
    try {
      const { data, error } = await supabase.functions.invoke('email-antwort-entwurf', {
        body: {
          aktion: 'ticket', modus,
          text: modus === 'ueberarbeiten' ? aktuellerText : undefined,
          anweisung: anweisung || undefined,
          ticket_id: kontext.ticketId, titel: kontext.titel,
        },
      });
      if (error) throw new Error(error.message || 'Fehler');
      if (data?.error) throw new Error(data.error);
      if (!data?.entwurf) throw new Error('Leerer Entwurf');
      setVorschau(data.entwurf);
    } catch (e: any) {
      setFehler(e?.message || 'KI-Anfrage fehlgeschlagen');
    } finally {
      setLaedt(null);
    }
  };

  const uebernehmen = () => {
    if (vorschau == null) return;
    onUebernehmen(vorschau);
    setVorschau(null); setAnweisung(''); setOffen(false);
  };

  return (
    // Dunkel (Pille): Fenster im Fluss statt schwebend – Abschnittskarten schneiden Überstand ab.
    <div className={dunkel && offen ? 'relative w-full' : 'relative'}>
      <button
        type="button"
        onClick={() => setOffen((v) => !v)}
        title={dunkel ? undefined : 'KI: Text überarbeiten oder entwerfen'}
        aria-label="KI: Text überarbeiten oder entwerfen"
        aria-expanded={offen}
        data-tip={dunkel ? 'KI – Text überarbeiten oder entwerfen' : undefined}
        className={dunkel ? 'p-1.5 rounded-lg transition-colors' : `p-1.5 rounded-lg transition-colors ${offen ? 'bg-purple-100 text-purple-700' : 'text-purple-500 hover:bg-purple-50 hover:text-purple-700'}`}
        style={dunkel ? { color: '#d59bff', background: offen ? 'rgba(191,90,242,0.2)' : 'transparent' } : undefined}
      >
        <Sparkles size={18} />
      </button>
      {offen && (
        <div className={dunkel
          ? 'mt-2 w-full rounded-2xl p-3 space-y-2.5'
          : `absolute ${richtung === 'oben' ? 'bottom-full mb-2' : 'top-full mt-2'} right-0 w-80 rounded-xl border border-gray-200 bg-white p-3 shadow-xl z-40 space-y-2.5`}
          style={dunkel ? { background: '#1c1c1e', boxShadow: '0 0 0 0.5px rgba(255,255,255,0.12), 0 18px 40px -12px rgba(0,0,0,0.7)', color: FORM.text } : undefined}>
          <p className={dunkel ? 'text-[10.5px] font-extrabold uppercase tracking-widest' : 'text-[9px] font-black uppercase tracking-widest text-gray-400'}
            style={dunkel ? { color: FORM.text3 } : undefined}>KI</p>
          {vorschau === null ? (
            <>
              <input
                type="text"
                value={anweisung}
                onChange={(e) => setAnweisung(e.target.value)}
                placeholder="Anweisung … (z. B. Statuskommentar: Handwerker kommt Do.)"
                className={dunkel
                  ? 'pf-klein w-full rounded-xl px-3 py-2 text-sm font-semibold outline-none placeholder:text-white/30'
                  : 'w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-purple-300'}
                style={dunkel ? { background: FELD.ruhe.flaeche, boxShadow: FELD.ruhe.ring, color: FORM.text } : undefined}
              />
              <div className="flex flex-wrap gap-1.5">
                {CHIPS.map((chip) => (
                  <button key={chip} type="button" onClick={() => setAnweisung(chip)}
                    className={dunkel ? 'rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors' : 'rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-200 transition-colors'}
                    style={dunkel ? { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.78)' } : undefined}>
                    {chip}
                  </button>
                ))}
              </div>
              {fehler && <p className="text-[12px] font-semibold text-red-500" style={dunkel ? { color: FORM.rot } : undefined}>{fehler}</p>}
              <div className="space-y-1.5 pt-0.5">
                <button
                  type="button"
                  disabled={laedt !== null || !aktuellerText.trim()}
                  onClick={() => rufen('ueberarbeiten')}
                  title={!aktuellerText.trim() ? 'Erst Text ins Feld schreiben' : undefined}
                  className={dunkel
                    ? 'w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12px] font-extrabold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed'
                    : 'w-full inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-[12px] font-bold text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'}
                  style={dunkel ? { background: KNOPF.gold.flaeche, color: KNOPF.gold.text } : undefined}
                >
                  {laedt === 'ueberarbeiten' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Meinen Text überarbeiten
                </button>
                <button
                  type="button"
                  disabled={laedt !== null || !anweisung.trim()}
                  onClick={() => rufen('entwerfen')}
                  title={!anweisung.trim() ? 'Erst eine Anweisung eingeben' : undefined}
                  className={dunkel
                    ? 'w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-[12px] font-extrabold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed'
                    : 'w-full inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-bold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'}
                  style={dunkel ? { background: KNOPF.grau.flaeche, color: KNOPF.grau.text } : undefined}
                >
                  {laedt === 'entwerfen' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Text entwerfen
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={dunkel ? 'max-h-64 overflow-y-auto pf-scroll rounded-xl p-2.5 text-[13px] leading-relaxed whitespace-pre-wrap' : 'max-h-64 overflow-y-auto custom-scrollbar rounded-lg border border-gray-200 bg-white p-2.5 text-[13px] leading-relaxed text-gray-800 whitespace-pre-wrap'}
                style={dunkel ? { background: FELD.ruhe.flaeche, boxShadow: FELD.ruhe.ring, color: 'rgba(255,255,255,0.85)' } : undefined}>
                {vorschau}
              </div>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={uebernehmen}
                  className={dunkel ? 'flex-1 rounded-xl px-3 py-2 text-[12px] font-extrabold' : 'flex-1 rounded-lg bg-purple-600 px-3 py-2 text-[12px] font-bold text-white hover:bg-purple-700 transition-colors'}
                  style={dunkel ? { background: KNOPF.gold.flaeche, color: KNOPF.gold.text } : undefined}>
                  Übernehmen
                </button>
                <button type="button" onClick={() => setVorschau(null)}
                  className={dunkel ? 'flex-1 rounded-xl px-3 py-2 text-[12px] font-extrabold' : 'flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-bold text-gray-700 hover:bg-gray-50 transition-colors'}
                  style={dunkel ? { background: KNOPF.grau.flaeche, color: KNOPF.grau.text } : undefined}>
                  Verwerfen
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
